/**
 * Install attribution (AppsFlyer), added 2026-10-13 for the Reddit Ads test.
 *
 * WHAT THIS IS FOR
 *   acquisition.ts already records the first touch for web visits and Play
 *   installs that carry a referrer. It cannot see a paid app-store install:
 *   Reddit (and Apple Ads, TikTok later) report their clicks to a measurement
 *   partner, and only that partner can say "this install came from that ad".
 *   AppsFlyer is that partner. Reddit reads installs and the three events
 *   below straight from AppsFlyer, which is what lets its campaigns optimise
 *   toward installs instead of clicks.
 *
 * WHAT IS SENT, AND WHAT IS NOT
 *   - Install, session, and exactly three events: af_complete_registration,
 *     first_scan, af_subscribe. No scan content, no dex rows, no trainer name,
 *     no email. The customer id is the numeric account id, same as Sentry.
 *   - iOS: the Podfile sets $AppsFlyerStrictMode, which removes IDFA collection
 *     and the AdSupport framework entirely. Nothing asks for App Tracking
 *     Transparency; Reddit attribution on iPhone comes through Apple's
 *     SKAdNetwork, which AppsFlyer registers for us.
 *   - Android: the advertising id (GAID) is collected, because Reddit reports
 *     its clicks server-side and GAID is how the install is matched back.
 *     The Play Data safety form must declare "Device or other IDs" for
 *     analytics/attribution; the privacy policy lists AppsFlyer as a processor.
 *   - Web: no-op. The SDK is only loaded inside the Capacitor shells.
 *
 * GATING
 *   Enabled iff VITE_APPSFLYER_DEV_KEY is set at build time (client/.env.production).
 *   The plugin is imported dynamically so a checkout without the package still
 *   builds, exactly like iap.ts does for RevenueCat.
 *
 * SDK 7 STARTUP MODEL
 *   init -> registerSessionReadyListener -> start() inside that callback.
 *   Events logged before start() would be dropped by the native SDK, so they
 *   queue here and flush once the session is ready.
 */
import { getPlatform } from "./platform";

const IOS_APP_ID = "6767402880";
const FIRST_SCAN_KEY = "nx.af.firstScanLogged";
const REGISTRATION_KEY = "nx.af.registrationLogged";
/** An account created within this window of signing in is a registration. */
const NEW_ACCOUNT_WINDOW_MS = 10 * 60 * 1000;

/**
 * The slice of the AppsFlyer SDK 7 surface this module uses, typed locally so
 * the app type-checks whether or not appsflyer-capacitor-plugin is installed
 * (the import below is dynamic, as iap.ts does for RevenueCat). Shapes follow
 * @appsflyer-sdk/js-core-plugin's generated method params.
 */
interface AppsFlyerSdk {
  init(params: { devKey: string; appId?: string | null }): Promise<void>;
  setShouldCollectDeviceName(params: { collect: boolean }): Promise<void>;
  setLogLevel(params: { logLevel: "none" | "error" | "warning" | "info" | "debug" | "verbose" }): Promise<void>;
  registerSessionReadyListener(onReady: () => void): Promise<void>;
  start(params?: { awaitResponse?: boolean }): Promise<void>;
  logEvent(params: { eventName: string; eventValues?: Record<string, unknown> | null }): Promise<void>;
  setCustomerUserId(params: { customerId: string }): Promise<void>;
  getAppsFlyerUID(): Promise<string | null>;
}

function readEnv(key: string): string | undefined {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const env = (import.meta as any)?.env;
  const val = env?.[key];
  return typeof val === "string" && val.length > 0 ? val : undefined;
}

const DEBUG = readEnv("VITE_APPSFLYER_DEBUG") === "1";
/** Diagnostic builds only: a breadcrumb per startup step, visible in the device console. */
function trace(msg: string, extra?: unknown): void {
  if (DEBUG) console.warn("[attribution]", msg, extra ?? "");
}

let sdk: AppsFlyerSdk | null = null;
let initPromise: Promise<boolean> | null = null;
let sessionReady = false;
let readyWaiters: Array<() => void> = [];
const queued: Array<{ eventName: string; eventValues?: Record<string, unknown> }> = [];

function whenReady(): Promise<void> {
  if (sessionReady) return Promise.resolve();
  return new Promise((resolve) => readyWaiters.push(resolve));
}

async function flushQueue(): Promise<void> {
  if (!sdk || !sessionReady) return;
  while (queued.length > 0) {
    const ev = queued.shift()!;
    try {
      await sdk.logEvent({ eventName: ev.eventName, eventValues: ev.eventValues ?? null });
      trace("logEvent sent", ev.eventName);
    } catch (err) {
      console.warn("[attribution] logEvent failed", ev.eventName, err);
    }
  }
}

/**
 * Initialise the SDK. Idempotent. Resolves true when attribution is live for
 * this build/platform, false when it is a no-op (web, no key, plugin missing).
 * Never throws: attribution must not be able to break boot.
 */
export function initAttribution(): Promise<boolean> {
  if (initPromise) return initPromise;
  initPromise = (async () => {
    const platform = getPlatform();
    trace("platform", platform);
    if (platform === "web") return false;
    const devKey = readEnv("VITE_APPSFLYER_DEV_KEY");
    if (!devKey) {
      console.warn("[attribution] VITE_APPSFLYER_DEV_KEY not set; attribution disabled.");
      return false;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let mod: any;
    try {
      mod = await import("appsflyer-capacitor-plugin" as any);
    } catch (err) {
      console.warn("[attribution] appsflyer-capacitor-plugin not installed; attribution disabled.", err);
      return false;
    }
    const af: AppsFlyerSdk | undefined = mod?.AppsFlyer ?? mod?.default;
    if (!af) return false;
    try {
      await af.init({ devKey, appId: platform === "ios" ? IOS_APP_ID : null });
      trace("init ok");
      // Diagnostic builds only (VITE_APPSFLYER_DEBUG=1 at build time): the
      // native SDK logs every request and response (logcat tag AppsFlyer_*,
      // the Xcode console on iOS). Never set for a store build.
      if (readEnv("VITE_APPSFLYER_DEBUG") === "1") {
        await af.setLogLevel({ logLevel: "verbose" }).catch(() => undefined);
      }
      // The device's marketing name is of no use to us and is one more field
      // not to collect.
      await af.setShouldCollectDeviceName({ collect: false }).catch(() => undefined);
      await af.registerSessionReadyListener(async () => {
        trace("session ready");
        try {
          await af.start();
          trace("start ok");
        } catch (err) {
          console.warn("[attribution] start failed", err);
          return;
        }
        sessionReady = true;
        const waiters = readyWaiters;
        readyWaiters = [];
        waiters.forEach((w) => w());
        void flushQueue();
      });
      sdk = af;
      trace("listener registered, waiting for session ready");
      return true;
    } catch (err) {
      console.warn("[attribution] init failed; attribution disabled.", err);
      return false;
    }
  })();
  return initPromise;
}

function logEvent(eventName: string, eventValues?: Record<string, unknown>): void {
  void initAttribution().then((live) => {
    if (!live) return;
    queued.push({ eventName, eventValues });
    if (sessionReady) void flushQueue();
  });
}

function flagRead(key: string, scope: string): boolean {
  try {
    return localStorage.getItem(key) === scope;
  } catch {
    return false;
  }
}

function flagWrite(key: string, scope: string): void {
  try {
    localStorage.setItem(key, scope);
  } catch {
    /* quota or private mode: the worst case is one duplicate event */
  }
}

/**
 * Tie this install to the signed-in account, and log the registration once
 * when the account is brand new. Called from SessionProvider whenever the
 * account changes, so email signup, Google and Apple sign-in all land here
 * without touching the auth flows themselves.
 */
export async function identifyAttribution(account: {
  id: number;
  createdAt: string | number | null | undefined;
}): Promise<void> {
  const live = await initAttribution();
  if (!live || !sdk) return;
  try {
    await sdk.setCustomerUserId({ customerId: String(account.id) });
  } catch (err) {
    console.warn("[attribution] setCustomerUserId failed", err);
  }
  const created =
    typeof account.createdAt === "number"
      ? account.createdAt
      : account.createdAt
        ? Date.parse(String(account.createdAt))
        : NaN;
  const scope = String(account.id);
  if (Number.isFinite(created) && Date.now() - created < NEW_ACCOUNT_WINDOW_MS && !flagRead(REGISTRATION_KEY, scope)) {
    flagWrite(REGISTRATION_KEY, scope);
    logEvent("af_complete_registration", { af_registration_method: "app" });
  }
}

/**
 * The first time scan-sourced dex rows reach the server from this device.
 * Mirrors the server's own "has completed a scan" test
 * (cloudflare/src/lib/referralRewards.ts hasCompletedScan: a row with
 * source 'scan'). Once per device; the flag is not per account on purpose,
 * because the install is what Reddit is measuring.
 */
export function noteScanRowsSynced(): void {
  if (flagRead(FIRST_SCAN_KEY, "1")) return;
  flagWrite(FIRST_SCAN_KEY, "1");
  logEvent("first_scan");
}

/**
 * A completed Pro / Passport purchase. No revenue is attached here: the
 * RevenueCat -> AppsFlyer integration reports purchases, renewals and trials
 * server-side with the real amounts once it is switched on in the RevenueCat
 * dashboard (iap.ts hands RevenueCat the AppsFlyer id for that). This event
 * is the on-device signal Reddit can optimise toward in the meantime.
 */
export function trackSubscription(productId: string): void {
  logEvent("af_subscribe", { af_content_id: productId });
}

/** The AppsFlyer device id, for the RevenueCat integration. Null when attribution is off. */
export async function getAttributionId(): Promise<string | null> {
  const live = await initAttribution();
  if (!live || !sdk) return null;
  try {
    await whenReady();
    return (await sdk.getAppsFlyerUID()) ?? null;
  } catch {
    return null;
  }
}
